import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { getArticleById, getRelatedArticles, getLatestArticles } from '@/mocks/articles';

export default function Article() {
  const { id } = useParams<{ id: string }>();
  const article = id ? getArticleById(id) : undefined;
  const related = article ? getRelatedArticles(article.id, article.categorySlug, 3) : [];
  const latestSidebar = getLatestArticles(5);

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-16 text-center">
          <h2 className="text-xl font-heading font-bold text-foreground-950">文章不存在</h2>
          <Link to="/" className="text-sm text-primary-500 hover:underline mt-4 inline-block">
            返回首页
          </Link>
        </div>
        <Footer />
      </>
    );
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}年${d.getMonth() + 1}月${d.getDate()}日`;
  };

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.imageUrl,
    datePublished: article.publishedAt,
    author: {
      '@type': 'Person',
      name: article.authorName,
    },
    publisher: {
      '@type': 'Organization',
      name: '黑料吃瓜网',
    },
    url: `https://www.heiliaochigua.com/article/${article.id}`,
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: `${article.title}的相关背景是什么？`,
        acceptedAnswer: {
          '@type': 'Answer',
          text: article.excerpt,
        },
      },
    ],
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <Navbar />

      <main className="pt-24 pb-16">
        <div className="px-4 md:px-6 max-w-[1280px] mx-auto">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: article.categoryName, href: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <article className="lg:col-span-8">
              <header className="mb-6">
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-accent-100 text-accent-700 hover:bg-accent-200 transition-colors"
                  >
                    {article.categoryName}
                  </Link>
                  {article.isHot && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-primary-100 text-primary-600">
                      热门
                    </span>
                  )}
                </div>
                <h1 className="text-xl md:text-2xl lg:text-3xl font-heading font-bold text-foreground-950 leading-tight">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 mt-4 text-sm text-foreground-500 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-accent-100 flex items-center justify-center">
                      <i className="ri-user-line text-accent-500 text-sm"></i>
                    </div>
                    <span>{article.authorName}</span>
                  </div>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-calendar-line"></i>
                    {formatDate(article.publishedAt)} {formatTime(article.publishedAt)}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    {article.viewCount >= 10000
                      ? `${(article.viewCount / 10000).toFixed(1)}万阅读`
                      : `${article.viewCount}阅读`}
                  </span>
                  <span className="text-foreground-300">|</span>
                  <span className="flex items-center gap-1">
                    <i className="ri-chat-3-line"></i>
                    {article.commentCount}条评论
                  </span>
                </div>
              </header>

              <div className="rounded-xl overflow-hidden mb-8">
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  title={`${article.title} - 黑料吃瓜网`}
                  className="w-full aspect-video object-cover object-top"
                />
              </div>

              <div
                className="prose prose-base max-w-none text-foreground-800 leading-relaxed
                  prose-p:my-4 prose-p:leading-loose prose-p:text-foreground-800
                  prose-strong:text-foreground-950 prose-strong:font-semibold
                  [&_p]:text-[15px] md:[&_p]:text-base"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="flex flex-wrap gap-2 mt-8 pt-6 border-t border-background-200">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-full text-xs bg-background-100 text-foreground-600"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 p-5 rounded-xl bg-background-100 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center shrink-0">
                  <i className="ri-user-line text-accent-500 text-lg"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground-900">{article.authorName}</p>
                  <p className="text-xs text-foreground-500 mt-0.5">
                    黑料吃瓜网特约作者，专注娱乐资讯报道
                  </p>
                </div>
              </div>
            </article>

            <aside className="lg:col-span-4">
              <div className="sticky top-24">
                <div className="bg-background-100 rounded-xl p-5 mb-6">
                  <h3 className="text-base font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-fire-line text-accent-500"></i>
                    最新文章
                  </h3>
                  <div className="space-y-1">
                    {latestSidebar.map((a) => (
                      <ArticleCard key={a.id} article={a} variant="compact" />
                    ))}
                  </div>
                </div>

                {related.length > 0 && (
                  <div className="bg-background-100 rounded-xl p-5">
                    <h3 className="text-base font-heading font-bold text-foreground-950 mb-4 flex items-center gap-2">
                      <i className="ri-links-line text-accent-500"></i>
                      相关文章
                    </h3>
                    <div className="space-y-1">
                      {related.map((a) => (
                        <ArticleCard key={a.id} article={a} variant="compact" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}